import React, { useState } from 'react';
import { CreditCard, Shield, Lock, CheckCircle, X } from 'lucide-react';
import { getApiBase } from '../lib/apiBase';

const TABS = [
  { id: 'usa', label: 'USA (Stripe)', processor: 'Stripe', currency: 'USD' },
  { id: 'international', label: 'International (Scotiabank)', processor: 'Fiserv IPG (Scotiabank)', currency: 'USD' },
];

const PaymentCenter = () => {
  const [activeTab, setActiveTab] = useState('usa');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [formData, setFormData] = useState({
    invoiceNo: '',
    firstName: '',
    lastName: '',
    email: '',
    amount: '',
  });

  const activeTabData = TABS.find(t => t.id === activeTab);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // single-flight: never allow concurrent checkout requests
    setIsSubmitting(true);
    setStatusMessage('');

    // Resolve API base URL via the shared resolver (runtime config → env → origin).
    const API = getApiBase();
    const originUrl = window.location.origin;

    // One reference per submission, reused across retries. The backend uses it
    // as a Stripe idempotency key, so retries can NEVER create duplicate sessions.
    const clientReference = (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    // Map HTTP status → precise, user-visible reason.
    const messageForStatus = (status) => {
      if (status === 401) return 'Payment service authentication error. No payment was processed. Please contact support with reference SAG-401.';
      if (status === 403) return 'Payment service access denied. No payment was processed. Please contact support with reference SAG-403.';
      if (status === 404) return 'The payment service is temporarily unavailable. No payment was processed. Please try again shortly.';
      if (status === 429) return 'Too many attempts. No payment was processed. Please wait a moment and try again.';
      if (status === 500) return 'Payment service internal error. No payment was processed. Please contact support with reference SAG-500.';
      if (status === 0 || status === 502 || status === 503 || status === 504) return 'The payment service is temporarily unavailable. No payment was processed. Please try again shortly.';
      return `The payment service returned an unexpected error (HTTP ${status}). No payment was processed. Please contact support.`;
    };

    setStatusMessage('Connecting to payment service…');

    // Limited retries, ONLY for clearly transient failures (network drop or
    // ingress 404/429). Gateway/server errors (5xx) stop immediately — blind
    // retries of a checkout-creation request are unsafe.
    const postWithRetry = async (path, body, { tries = 3, initialDelay = 2000 } = {}) => {
      let lastStatus = 0;
      let lastData = null;
      for (let i = 0; i < tries; i++) {
        try {
          const res = await fetch(`${API}${path}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });
          const text = await res.text();
          let data;
          try { data = JSON.parse(text); }
          catch { data = null; }
          lastStatus = res.status;
          lastData = data;
          if (res.ok && data) return { ok: true, data };
          // Gateway/server errors (5xx) — stop immediately with an accurate message.
          if (res.status >= 500) {
            throw new Error((data && data.detail && `${messageForStatus(res.status)} [${data.detail}]`) || messageForStatus(res.status));
          }
          const transient = res.status === 404 || res.status === 429;
          if (transient && i < tries - 1) {
            const wait = initialDelay * Math.pow(2, i);
            setStatusMessage(`Payment service unavailable (HTTP ${res.status}). Retrying in ${Math.round(wait / 1000)}s… (attempt ${i + 2}/${tries})`);
            await new Promise(r => setTimeout(r, wait));
            continue;
          }
          throw new Error((data && data.detail) || messageForStatus(res.status));
        } catch (err) {
          if (err.message && err.message.includes('No payment was processed')) throw err;
          if (i < tries - 1 && (err.name === 'TypeError' || err.name === 'AbortError')) {
            const wait = initialDelay * Math.pow(2, i);
            setStatusMessage(`Network interruption. Retrying in ${Math.round(wait / 1000)}s… (attempt ${i + 2}/${tries})`);
            await new Promise(r => setTimeout(r, wait));
            continue;
          }
          if (i === tries - 1) {
            throw new Error(messageForStatus(lastStatus) + (lastData?.detail ? ` (${lastData.detail})` : ''));
          }
          throw err;
        }
      }
      throw new Error(messageForStatus(lastStatus));
    };

    try {
      if (activeTab === 'usa') {
        const { data } = await postWithRetry('/api/payments/checkout', {
          amount: parseFloat(formData.amount),
          currency: 'usd',
          invoice_number: formData.invoiceNo,
          email: formData.email,
          name: `${formData.firstName} ${formData.lastName}`,
          origin_url: originUrl,
          client_reference: clientReference,
        });
        if (data && data.url) { window.location.href = data.url; return; }
        throw new Error((data && data.detail) || 'No checkout URL returned');
      } else {
        const { data } = await postWithRetry('/api/payments/fiserv/checkout', {
          amount: parseFloat(formData.amount),
          invoice_number: formData.invoiceNo,
          email: formData.email,
          name: `${formData.firstName} ${formData.lastName}`,
          origin_url: originUrl,
        });
        if (data && data.gateway_url && data.form_fields) {
          const form = document.createElement('form');
          form.method = 'POST'; form.action = data.gateway_url;
          Object.entries(data.form_fields).forEach(([k, v]) => {
            const input = document.createElement('input');
            input.type = 'hidden'; input.name = k; input.value = v;
            form.appendChild(input);
          });
          document.body.appendChild(form); form.submit(); return;
        }
        throw new Error((data && data.detail) || 'No gateway URL returned');
      }
    } catch (err) {
      setStatusMessage(err.message || 'Payment error');
    }
    setIsSubmitting(false);
  };

  const inputClass = 'w-full px-4 py-3 bg-white border border-gray-200 text-sm text-black placeholder-gray-400 focus:outline-none focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227]/20 transition-colors';
  const labelClass = 'block text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-2';

  return (
    <section
      className="py-16 md:py-24 bg-white"
      data-testid="payment-center"
      id="payment-center"
    >
      <div className="max-w-lg mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#C9A227]/10 flex items-center justify-center mx-auto mb-5">
            <CreditCard className="w-7 h-7 text-[#C9A227]" />
          </div>
          <h2
            className="text-3xl sm:text-4xl font-light text-black mb-3"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            data-testid="payment-center-title"
          >
            Payment Center
          </h2>
          <p className="text-gray-500 text-sm">
            Make payment on your existing invoice or estimate.
          </p>
        </div>

        {/* Trust indicators */}
        <div className="flex flex-wrap items-center justify-center gap-6 mb-8">
          <div className="flex items-center gap-2 text-gray-500 text-xs">
            <Shield className="w-3.5 h-3.5 text-[#1e3a5f]" />
            <span>SSL Secured</span>
          </div>
          <div className="flex items-center gap-2 text-gray-500 text-xs">
            <Lock className="w-3.5 h-3.5 text-[#1e3a5f]" />
            <span>256-bit Encryption</span>
          </div>
          <div className="flex items-center gap-2 text-gray-500 text-xs">
            <CheckCircle className="w-3.5 h-3.5 text-[#1e3a5f]" />
            <span>PCI Compliant</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6" data-testid="payment-tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 text-[10px] sm:text-xs tracking-[0.1em] uppercase font-medium transition-all border-b-2 ${
                activeTab === tab.id
                  ? 'border-[#C9A227] text-[#0a0a0a] bg-slate-50/50'
                  : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
              data-testid={`payment-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Processor indicator */}
        <div className="text-center mb-6">
          <span className="text-[10px] tracking-wider uppercase text-gray-400">
            Processing via: <span className="text-[#1e3a5f] font-medium">{activeTabData.processor}</span>
          </span>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="payment-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>First Name *</label>
              <input
                type="text" name="firstName" required
                value={formData.firstName} onChange={handleChange}
                className={inputClass}
                data-testid="payment-first-name"
              />
            </div>
            <div>
              <label className={labelClass}>Last Name *</label>
              <input
                type="text" name="lastName" required
                value={formData.lastName} onChange={handleChange}
                className={inputClass}
                data-testid="payment-last-name"
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Email *</label>
            <input
              type="email" name="email" required
              value={formData.email} onChange={handleChange}
              placeholder="name@company.com"
              className={inputClass}
              data-testid="payment-email"
            />
          </div>

          <div>
            <label className={labelClass}>Invoice / Estimate No. *</label>
            <input
              type="text" name="invoiceNo" required
              value={formData.invoiceNo} onChange={handleChange}
              placeholder="INV-001"
              className={inputClass}
              data-testid="payment-invoice-no"
            />
          </div>

          <div>
            <label className={labelClass}>Amount ({activeTabData.currency}) *</label>
            <input
              type="number" name="amount" required min="0.01" step="0.01"
              value={formData.amount} onChange={handleChange}
              placeholder="0.00"
              className={inputClass}
              data-testid="payment-amount"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-4 bg-[#0a0a0a] text-white text-[11px] tracking-[0.15em] uppercase font-medium hover:bg-[#C9A227] hover:text-[#0a0a0a] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 mt-2"
            data-testid="payment-submit-btn"
          >
            {isSubmitting ? 'Processing...' : 'Proceed to Payment'}
          </button>

          {statusMessage && (
            <p
              className="text-center text-xs text-[#C9A227] mt-3"
              role="status"
              aria-live="polite"
              data-testid="payment-status-message"
            >
              {statusMessage}
            </p>
          )}

          <p className="text-center text-[10px] text-gray-300 mt-3">
            Visa, MasterCard, American Express &bull; Processed via {activeTabData.processor}
          </p>
        </form>
      </div>
    </section>
  );
};

export default PaymentCenter;
